import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import { fulfillmentOptions } from 'constants/fulfillmentOptions';
import LocationsCard from 'components/Location/LocationsCard';
import isLocationClosed, {
  getIsLocationAcceptingOrdersForMethodSelector
} from 'bento-ordering/locations/utils/isLocationClosed';
import LocationCardDineInFulfillmentMethods from 'bento-ordering/locations/components/LocationCardDineInFullfilmentMethods';
import LocationCardOnlineOrderingFulfillmentMethods from 'bento-ordering/locations/components/LocationCardOnlineOrderingFulfillmentMethods';
import getApplicationVariant, {
  APPLICATION_VARIANTS
} from 'bento-ordering/utils/getApplicationVariant';

function CardActions(props) {
  const applicationVariant = getApplicationVariant();
  let Component = null;

  if (applicationVariant === APPLICATION_VARIANTS.ONLINE_ORDERING) {
    Component = LocationCardOnlineOrderingFulfillmentMethods;
  } else if (applicationVariant === APPLICATION_VARIANTS.DINE_IN) {
    Component = LocationCardDineInFulfillmentMethods;
  }

  return <Component {...props} />;
}

function OnlineOrderingLocationCard({
  locationObject,
  isSelected,
  beginOrder
}) {
  const locationDetail = locationObject.location;

  return (
    <LocationsCard
      cardActions={
        <CardActions
          beginOrder={fulfillmentMethod => {
            beginOrder(locationObject.id, fulfillmentMethod);
          }}
          doesDeliverToAddress={
            // only pass through the value if it exists on the location objects
            // otherwise delivery will be disabled by default
            Object.prototype.hasOwnProperty.call(
              locationObject,
              'does_deliver_to_address'
            )
              ? locationObject.does_deliver_to_address
              : true
          }
          firstAvailablePickupDateOption={get(
            locationObject,
            'fulfillment_options.pickup.datetimes_with_ranges[0].date'
          )}
          firstAvailableDeliveryDateOption={get(
            locationObject,
            'fulfillment_options.delivery.datetimes_with_ranges[0].date'
          )}
          firstAvailableDineInDateOption={get(
            locationObject,
            'fulfillment_options.dine_in.datetimes_with_ranges[0].date'
          )}
          firstAvailableNoAsapPickupDateOption={get(
            locationObject,
            'open_pickup_hours.first_available_asap.first_date_available'
          )}
          firstAvailableNoAsapDeliveryDateOption={get(
            locationObject,
            'open_delivery_hours.first_available_asap.first_date_available'
          )}
          id={locationObject.id}
          isAcceptingPickupOrders={getIsLocationAcceptingOrdersForMethodSelector(
            locationObject,
            fulfillmentOptions.PICKUP
          )}
          isAcceptingDeliveryOrders={getIsLocationAcceptingOrdersForMethodSelector(
            locationObject,
            fulfillmentOptions.DELIVERY
          )}
          isAcceptingDineInOrders={getIsLocationAcceptingOrdersForMethodSelector(
            locationObject,
            fulfillmentOptions.DINE_IN
          )}
          isAsapPickupCurrentlyAvailable={get(
            locationObject,
            'fulfillment_options.pickup.is_asap_available'
          )}
          isAsapDeliveryCurrentlyAvailable={get(
            locationObject,
            'fulfillment_options.delivery.is_asap_available'
          )}
          isAsapDineInCurrentlyAvailable={get(
            locationObject,
            'fulfillment_options.dine_in.is_asap_available'
          )}
          isClosed={isLocationClosed(locationObject)}
          openDisplayPickupHours={locationObject.open_pickup_hours}
          openDisplayDeliveryHours={locationObject.open_delivery_hours}
          openDisplayDineInHours={locationObject.open_dine_in_hours}
          isAcceptingLaterOrders={locationObject.accepting_later_orders}
          isAcceptingOrders={locationObject.accepting_orders}
        />
      }
      id={locationObject.id}
      name={locationDetail.name}
      distance={
        locationObject.distance_to_address
          ? `${locationObject.distance_to_address} miles`
          : ''
      }
      staticMapsURLs={locationDetail.google_maps_static_urls}
      description={locationObject.description}
      phoneNumber={locationDetail.phone_number}
      street={locationDetail.street}
      city={locationDetail.city}
      state={locationDetail.state}
      postalCode={locationDetail.postal_code}
      lat={locationDetail.lat}
      lng={locationDetail.lng}
      isSelected={isSelected}
    />
  );
}

OnlineOrderingLocationCard.propTypes = {
  locationObject: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    location: PropTypes.shape({
      name: PropTypes.string,
      lat: PropTypes.string,
      lng: PropTypes.string,
      street: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      postal_code: PropTypes.string,
      phone_number: PropTypes.string,
      google_maps_static_urls: PropTypes.object
    }).isRequired,
    description: PropTypes.string,
    is_closed: PropTypes.bool,
    distance_to_address: PropTypes.string,
    does_deliver_to_address: PropTypes.bool,
    open_pickup_hours: PropTypes.object.isRequired,
    open_delivery_hours: PropTypes.object.isRequired,
    open_dine_in_hours: PropTypes.object.isRequired,
    accepting_later_orders: PropTypes.bool.isRequired,
    accepting_orders: PropTypes.bool.isRequired
  }),
  isSelected: PropTypes.bool.isRequired,
  beginOrder: PropTypes.func.isRequired
};

export default OnlineOrderingLocationCard;
